import { useState , useEffect} from "react";
import { useParams, Link } from "react-router-dom";
import {useSelector} from "react-redux";
import {
  TextField,
  Button,
  Container,
  Typography,
  Paper,
  Alert,
  Box,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  List,
} from "@mui/material";
import axiosInstance from "../../utils/axiosInstance";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Cancel from "@mui/icons-material/Cancel";
import {logger} from "../../utils/logger";

export default function ResetPasswordForm() {
  const { token } = useParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const passwordRules = {
    minLength: password.length >= 6,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

    useEffect(() => {
    logger.info("Reset Password form opened", { token });
    return () => {
      logger.info("Reset Password form closed", { token });
    };
  }, [token]);

  const handleReset = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    if (
      !passwordRules.minLength ||
      !passwordRules.hasUppercase ||
      !passwordRules.hasLowercase ||
      !passwordRules.hasNumber ||
      !passwordRules.hasSpecialChar
    ) {
      setError("Password does not meet all requirements.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    try {
      await axiosInstance.post(`/api/auth/reset-password/${token}`, {
        newPassword: password,
      });
      setMessage("Password has been reset successfully.");
        logger.info("Password reset successfully", {
        token,
      });
    } catch (err) {
      setError(err.response?.data?.message || "Token expired or invalid.");
      logger.error("Password reset failed", {
        token,
        error: err,
      });
    }
  };

  const renderChecklistItem = (condition, text) => (
    <ListItem>
      <ListItemIcon>
        {condition ? <CheckCircle color="success" /> : <Cancel color="error" />}
      </ListItemIcon>
      <ListItemText primary={text} />
    </ListItem>
  );

  return (
    <Container
      maxWidth="xs"
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Paper elevation={3} sx={{ p: 4, width: "100%", borderRadius: 2 }}>
        <Typography variant="h5" align="center" gutterBottom>
          Reset Your Password
        </Typography>
        <Typography
          variant="body2"
          align="center"
          sx={{ mb: 2, color: "text.secondary" }}
        >
          Enter your new password below.
        </Typography>
        <form onSubmit={handleReset}>
          <TextField
            fullWidth
            margin="normal"
            type="password"
            label="New Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoFocus
          />
          <TextField
            fullWidth
            margin="normal"
            type="password"
            label="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
          {/* Password checklist */}
          <Box mb={2} p={1} border="1px solid #ccc" borderRadius={2}>
            <Typography variant="caption" fontWeight="bold" fontSize="0.75rem">
              Password must contain:
            </Typography>
            <List dense sx={{ paddingTop: 0, paddingBottom: 0 }}>
              {renderChecklistItem(
                passwordRules.minLength,
                "At least 6 characters"
              )}
              {renderChecklistItem(
                passwordRules.hasUppercase,
                "An uppercase letter (A-Z)"
              )}
              {renderChecklistItem(
                passwordRules.hasLowercase,
                "A lowercase letter (a-z)"
              )}
              {renderChecklistItem(passwordRules.hasNumber, "A number (0-9)")}
              {renderChecklistItem(
                passwordRules.hasSpecialChar,
                "A special character (!@#$...)"
              )}
            </List>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Button type="submit" variant="contained" fullWidth color="primary">
              Reset Password
            </Button>
          </Box>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          {message && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {message}
            </Alert>
          )}
        </form>
        {/* Login link */}
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Link
            to="/login"
            style={{
              textDecoration: "none",
              color: "#1976d2",
              fontSize: "0.95rem",
            }}
          >
            Back to Login
          </Link>
        </Box>
      </Paper>
    </Container>
  );
}
